import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConfig, setConfig, getPrompts, createPrompt, activatePrompt, Prompt, ConfigValue } from '../lib/api';
import { Save, Plus, Check, Loader2, AlertCircle } from 'lucide-react';

export default function Configuration() {
  const [activeTab, setActiveTab] = useState<'config' | 'prompts'>('config');
  const [selectedConfig, setSelectedConfig] = useState<'grid' | 'scoring' | 'keywords'>('grid');
  const [configForm, setConfigForm] = useState<ConfigValue>({});
  const [newPromptBlock, setNewPromptBlock] = useState('keyword_generation');
  const [newPromptContent, setNewPromptContent] = useState('');

  const queryClient = useQueryClient();

  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: ['config', selectedConfig],
    queryFn: () => getConfig(selectedConfig),
  });

  const { data: promptsData, isLoading: promptsLoading } = useQuery({
    queryKey: ['prompts'],
    queryFn: getPrompts,
  });

  const setConfigMutation = useMutation({
    mutationFn: ({ key, value }: { key: 'grid' | 'scoring' | 'keywords'; value: ConfigValue }) => 
      setConfig(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['config', selectedConfig] });
    },
  });

  const createPromptMutation = useMutation({
    mutationFn: ({ block, content }: { block: string; content: string }) => 
      createPrompt(block, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
      setNewPromptContent('');
    },
  });

  const activatePromptMutation = useMutation({
    mutationFn: activatePrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prompts'] });
    },
  });

  const handleConfigSave = () => {
    setConfigMutation.mutate({ key: selectedConfig, value: configForm });
  };

  const handlePromptCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPromptContent.trim()) return;
    createPromptMutation.mutate({ block: newPromptBlock, content: newPromptContent });
  };

  if (configLoading || promptsLoading) {
    return <div className="flex items-center justify-center h-64 text-slate-500">Loading configuration...</div>;
  }

  // Initialize form when configData loads
  if (configData && Object.keys(configForm).length === 0) {
    setConfigForm(configData);
  }

  const renderJsonValue = (obj: any, path: string[] = []): React.ReactNode => {
    if (typeof obj !== 'object' || obj === null) {
      return (
        <div className="flex items-center gap-2 mb-2" key={path.join('.')}>
          <label className="w-1/3 text-sm font-mono text-emerald-400 truncate" title={path.join('.')}>
            {path.join('.')}
          </label>
          <input
            type={typeof obj === 'number' ? 'number' : 'text'}
            step={typeof obj === 'number' ? '0.01' : undefined}
            className="flex-1 bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            value={obj}
            onChange={(e) => {
              const val = typeof obj === 'number' ? parseFloat(e.target.value) : e.target.value;
              setConfigForm((prev: any) => {
                const next = { ...prev };
                let current = next;
                for (let i = 0; i < path.length - 1; i++) {
                  current = current[path[i]];
                }
                current[path[path.length - 1]] = val;
                return next;
              });
            }}
          />
        </div>
      );
    }
    return Object.entries(obj).map(([key, val]) => renderJsonValue(val, [...path, key]));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">Service Configuration</h1>
        <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'config' 
                ? 'bg-emerald-500/10 text-emerald-400' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            Parameters
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'prompts' 
                ? 'bg-emerald-500/10 text-emerald-400' 
                : 'text-slate-400 hover:text-slate-200'
            }`}
          >
            LLM Prompts
          </button>
        </div>
      </div>

      {activeTab === 'config' && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="flex gap-2 mb-6">
            {(['grid', 'scoring', 'keywords'] as const).map((key) => (
              <button
                key={key}
                onClick={() => { setSelectedConfig(key); setConfigForm({}); }}
                className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize transition-colors ${
                  selectedConfig === key 
                    ? 'bg-slate-800 text-slate-100' 
                    : 'text-slate-400 hover:bg-slate-800/50'
                }`}
              >
                {key}
              </button>
            ))}
          </div>

          {configLoading ? (
            <div className="text-slate-500">Loading...</div>
          ) : (
            <div className="space-y-4">
              {renderJsonValue(configForm)}
              <div className="pt-4 border-t border-slate-800 flex justify-end">
                <button
                  onClick={handleConfigSave}
                  disabled={setConfigMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors disabled:opacity-50"
                >
                  {setConfigMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'prompts' && (
        <div className="space-y-6">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold mb-4 text-slate-100">Add New Prompt Version</h2>
            <form onSubmit={handlePromptCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Target Block</label>
                <select
                  value={newPromptBlock}
                  onChange={(e) => setNewPromptBlock(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="keyword_generation">keyword_generation</option>
                  <option value="content_quality">content_quality</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Prompt Content</label>
                <textarea
                  value={newPromptContent}
                  onChange={(e) => setNewPromptContent(e.target.value)}
                  rows={8}
                  className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  placeholder="Enter the system prompt..."
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={createPromptMutation.isPending || !newPromptContent.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors disabled:opacity-50"
                >
                  {createPromptMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  Create Version
                </button>
              </div>
            </form>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800">
              <h2 className="text-lg font-semibold text-slate-100">Prompt History</h2>
            </div>
            <div className="divide-y divide-slate-800">
              {promptsData?.items.map((prompt: Prompt) => (
                <div key={prompt.key} className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-mono text-sm text-emerald-400">{prompt.key}</h3>
                      <p className="text-xs text-slate-500">
                        Updated: {new Date(prompt.updated_at).toLocaleString()}
                      </p>
                    </div>
                    {prompt.is_active ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400">
                        <Check className="h-3 w-3" /> Active
                      </span>
                    ) : (
                      <button
                        onClick={() => activatePromptMutation.mutate(prompt.key)}
                        disabled={activatePromptMutation.isPending}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-md transition-colors disabled:opacity-50"
                      >
                        {activatePromptMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Set Active'}
                      </button>
                    )}
                  </div>
                  <pre className="bg-slate-950 p-4 rounded-md text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap">
                    {prompt.content}
                  </pre>
                </div>
              ))}
              {(!promptsData?.items || promptsData.items.length === 0) && (
                <div className="p-8 text-center text-slate-500 flex flex-col items-center gap-2">
                  <AlertCircle className="h-8 w-8 text-slate-600" />
                  <p>No prompts configured yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
